import { Button, LoadingOverlay, Stack, Text } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { PasswordInput } from '@components';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { useQuery } from '@hooks/useQuery';
import { useCheckResetPasswordToken, useResetPassword } from '@hooks/user';
import { passwordSchema } from '@validators';
import { AuthLayout } from './AuthLayout';

const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    passwordConfirmation: z.string().min(1, { message: 'Please confirm your password' }),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'Passwords do not match',
    path: ['passwordConfirmation'],
  });

type ResetPasswordFormValues = Required<z.infer<typeof resetPasswordSchema>>;

export const ResetPassword = () => {
  const navigate = useNavigate();
  const token = useQuery().get('resetToken') ?? '';
  const { data: { valid, message } = {}, isLoading } = useCheckResetPasswordToken(token);
  const resetPasswordMutation = useResetPassword(() => navigate('/auth'));

  const form = useFormWithNameInputProp<ResetPasswordFormValues>({
    initialValues: { password: '', passwordConfirmation: '' },
    validate: zodResolver(resetPasswordSchema),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  const onSubmit = (values: ResetPasswordFormValues) => {
    resetPasswordMutation.mutate({ ...values, resetToken: token });
  };

  return (
    <AuthLayout title="Reset password">
      <LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ radius: 'sm', blur: 2 }} />
      <form onSubmit={form.onSubmit(onSubmit)}>
        {valid === false && <Text c="red">{message ?? 'Invalid reset token'}</Text>}
        {valid && (
          <Stack gap="md">
            <PasswordInput {...form.getInputProps('password')} withAsterisk label="New password" type="password" size="lg" />
            <PasswordInput {...form.getInputProps('passwordConfirmation')} withAsterisk label="Confirm new password" type="password" size="lg" />
            {resetPasswordMutation.isError && (
              <Text c="red" fw={700} data-pw="reset-password-error">
                {resetPasswordMutation.error.response?.data?.message ?? 'Reset password failed'}
              </Text>
            )}
            <Button type="submit" variant="filled" size="lg">
              Reset password
            </Button>
          </Stack>
        )}
      </form>
      <Text ta="right" mt="md">
        <Link to={'/auth'}>← Back to login</Link>
      </Text>
    </AuthLayout>
  );
};
